import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BarChart3, 
  Ticket, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Users,
  TrendingUp,
  Calendar,
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
  Wrench,
  FileText,
  HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TicketReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [overdueTickets, setOverdueTickets] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [overdueHours, setOverdueHours] = useState(24);

  useEffect(() => {
    // Set default date range: 1 year ago to 1 year from now to capture all tickets
    const end = new Date();
    end.setFullYear(end.getFullYear() + 1);
    const start = new Date();
    start.setFullYear(start.getFullYear() - 1);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchStatistics();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchOverdueTickets();
  }, [overdueHours]);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/tickets/reports/statistics?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverdueTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/tickets/overdue?hours=${overdueHours}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setOverdueTickets(response.data.data.tickets || []);
      }
    } catch (error) {
      console.error('Error fetching overdue tickets:', error);
    }
  };

  const getStatusConfig = (status) => {
    const config = {
      'OPEN': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', bar: 'bg-blue-500' },
      'PENDING': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', bar: 'bg-amber-500' },
      'IN_PROGRESS': { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', bar: 'bg-indigo-500' },
      'CLOSE': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500' },
      'CANCELLED': { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', bar: 'bg-slate-400' }
    };
    return config[status] || config['OPEN'];
  };

  const getTypeConfig = (type) => {
    const config = {
      'PEMASANGAN': { icon: Wrench, color: 'text-blue-600', bar: 'bg-blue-500' },
      'PROBLEM': { icon: AlertCircle, color: 'text-amber-600', bar: 'bg-amber-500' },
      'REQUEST': { icon: FileText, color: 'text-indigo-600', bar: 'bg-indigo-500' }
    };
    return config[type] || { icon: HelpCircle, color: 'text-slate-600', bar: 'bg-slate-500' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-600 border-t-transparent mx-auto mb-3"></div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Memuat laporan...</p>
        </div>
      </div>
    );
  }

  const totalPending = (stats?.by_status?.OPEN || 0) + (stats?.by_status?.PENDING || 0) + (stats?.by_status?.IN_PROGRESS || 0);

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-slate-50 min-h-screen dark:bg-slate-900">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/app/tickets')}
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 mb-2 text-sm transition-colors dark:text-slate-400"
          >
            <ArrowLeft size={16} />
            <span>Kembali</span>
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-200">Laporan Tiket</h1>
          <p className="text-sm text-slate-500 mt-0.5 dark:text-slate-400">Statistik dan analisis performa tiket</p>
        </div>
        <button
          onClick={() => { fetchStatistics(); fetchOverdueTickets(); }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm text-sm font-medium"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Date Filter */}
      <Card className="shadow-sm border-slate-200 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <Calendar size={16} />
              <span className="text-sm font-medium">Periode:</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:border-slate-700 dark:bg-slate-800"
              />
              <span className="text-slate-400 dark:text-slate-500">—</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:border-slate-700 dark:bg-slate-800"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats - Redesigned */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide dark:text-slate-400">Total Tiket</p>
                <p className="text-3xl font-bold text-slate-800 mt-1 dark:text-slate-200">{stats?.total || 0}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">dalam periode ini</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-xl dark:bg-indigo-900/30">
                <Ticket className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide dark:text-slate-400">Selesai</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1 dark:text-emerald-400">{stats?.by_status?.CLOSE || 0}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">
                  {stats?.total ? Math.round((stats.by_status?.CLOSE || 0) / stats.total * 100) : 0}% completion
                </p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl dark:bg-emerald-900/30">
                <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide dark:text-slate-400">Pending</p>
                <p className="text-3xl font-bold text-amber-600 mt-1 dark:text-amber-400">{totalPending}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">butuh tindakan</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl dark:bg-amber-900/30">
                <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide dark:text-slate-400">Avg. Resolution</p>
                <p className="text-3xl font-bold text-indigo-600 mt-1 dark:text-indigo-400">{stats?.avg_resolution_time_hours || 0}<span className="text-lg font-medium">h</span></p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">waktu rata-rata</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-xl dark:bg-indigo-900/30">
                <TrendingUp className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Status */}
        <Card className="shadow-sm border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2 border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2 dark:text-slate-200">
              <BarChart3 size={16} className="text-indigo-500" />
              Berdasarkan Status
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {stats?.by_status && Object.entries(stats.by_status).map(([status, count]) => {
                const config = getStatusConfig(status);
                const percentage = stats.total ? Math.round((count / stats.total) * 100) : 0;
                
                return (
                  <div key={status} className="group">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}>
                          {status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{count}</span>
                        <span className="text-xs text-slate-400 dark:text-slate-500">({percentage}%)</span>
                      </div>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden dark:bg-slate-800">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${config.bar}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* By Type */}
        <Card className="shadow-sm border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2 border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2 dark:text-slate-200">
              <Ticket size={16} className="text-indigo-500" />
              Berdasarkan Tipe
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {stats?.by_type && Object.entries(stats.by_type).map(([type, count]) => {
                const config = getTypeConfig(type);
                const Icon = config.icon;
                const percentage = stats.total ? Math.round((count / stats.total) * 100) : 0;
                
                return (
                  <div key={type} className="group">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon size={16} className={config.color} />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{type}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{count}</span>
                        <span className="text-xs text-slate-400 dark:text-slate-500">({percentage}%)</span>
                      </div>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden dark:bg-slate-800">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${config.bar}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Teknisi Performance - Redesigned */}
      <Card className="shadow-sm border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2 dark:text-slate-200">
            <Users size={16} className="text-indigo-500" />
            Performa Teknisi
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Teknisi</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Total</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Open</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Closed</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Completion Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {stats?.by_teknisi && Object.entries(stats.by_teknisi).map(([name, data], index) => {
                  const rate = data.total > 0 ? Math.round((data.closed / data.total) * 100) : 0;
                  const rateColor = rate >= 80 ? 'text-emerald-600 bg-emerald-50' : rate >= 50 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
                  
                  return (
                    <tr key={name} className={`hover:bg-slate-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center dark:bg-indigo-900/40">
                            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                              {name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{data.total}</span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 bg-amber-50 text-amber-700 rounded-md text-xs font-semibold dark:bg-amber-900/30 dark:text-amber-300">
                          {data.open}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-semibold dark:bg-emerald-900/30 dark:text-emerald-300">
                          {data.closed}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[3rem] px-2.5 py-1 rounded-md text-xs font-bold ${rateColor}`}>
                          {rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {(!stats?.by_teknisi || Object.keys(stats.by_teknisi).length === 0) && (
                  <tr>
                    <td colSpan="5" className="px-5 py-8 text-center text-slate-400 text-sm dark:text-slate-500">
                      Tidak ada data teknisi
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Overdue Tickets - Redesigned */}
      <Card className="shadow-sm border-red-200 bg-white dark:bg-slate-800">
        <CardHeader className="pb-3 bg-red-50 border-b border-red-100 rounded-t-lg dark:bg-red-900/30">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-red-700 flex items-center gap-2 dark:text-red-300">
              <AlertTriangle size={16} />
              Tiket Overdue
              {overdueTickets.length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">
                  {overdueTickets.length}
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600 dark:text-red-400">Lebih dari</span>
              <select
                value={overdueHours}
                onChange={(e) => setOverdueHours(Number(e.target.value))}
                className="px-2.5 py-1.5 text-xs border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-red-700 font-medium dark:bg-slate-800 dark:text-red-300"
              >
                <option value={12}>12 jam</option>
                <option value={24}>24 jam</option>
                <option value={48}>48 jam</option>
                <option value={72}>72 jam</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {overdueTickets.length === 0 ? (
            <div className="p-10 text-center">
              <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3 dark:bg-emerald-900/30">
                <CheckCircle className="h-7 w-7 text-emerald-500" />
              </div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Tidak ada tiket overdue</p>
              <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Semua tiket dalam batas waktu</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-red-50/50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-red-600 uppercase tracking-wide dark:text-red-400">No. Tiket</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-red-600 uppercase tracking-wide dark:text-red-400">Customer</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-red-600 uppercase tracking-wide dark:text-red-400">Teknisi</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-red-600 uppercase tracking-wide dark:text-red-400">Status</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-red-600 uppercase tracking-wide dark:text-red-400">Overdue</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-red-600 uppercase tracking-wide dark:text-red-400">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-100">
                  {overdueTickets.map((ticket, index) => {
                    const statusConfig = getStatusConfig(ticket.status);
                    
                    return (
                      <tr key={ticket.id} className={`hover:bg-red-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-red-50/30'}`}>
                        <td className="px-5 py-3">
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">#{ticket.ticket_number}</span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{ticket.customer_name}</div>
                          <div className="text-xs text-slate-400 dark:text-slate-500">{ticket.customer_phone}</div>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-sm text-slate-600 dark:text-slate-300">{ticket.assigned_to_name || <span className="italic text-slate-400 dark:text-slate-500">Belum assign</span>}</span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.text} border ${statusConfig.border}`}>
                            {ticket.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-md text-xs font-bold dark:bg-red-900/40 dark:text-red-300">
                            <Clock size={12} />
                            {ticket.overdue_hours}h
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <button
                            onClick={() => navigate(`/app/tickets/${ticket.id}`)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
                          >
                            Lihat Detail
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketReports;
